import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { screenKey, noteFocus, restoreFocus, cancelRestore } from '@/lib/focus-memory.ts';

/**
 * Remember the cursor per screen, and put it back on the way in.
 *
 * MOUNTED ONCE, IN THE SHELL, and that is the point of doing it here rather
 * than in each page. Every screen has the same problem and would otherwise
 * grow the same twenty lines — and the two that forgot would be the two
 * somebody complains about. It watches the document rather than any
 * component, so a screen added next year gets this without knowing it exists.
 *
 * The listeners are passive and on the capture phase: `focusin` bubbles, but
 * capturing means a component that stops propagation cannot quietly break
 * the memory for its own subtree.
 */
/** True until the first screen of this launch has been given a cursor. */
let firstScreen = true;

export function useFocusMemory(): void {
  const location = useLocation();
  const key = screenKey(location.pathname, location.search);

  /*
   * Note whatever takes the cursor — but only inside the CONTENT.
   *
   * Chrome is deliberately excluded, and the reason is a bug this caused:
   * `focusin` fires on the "Live TV" link BEFORE the route changes, so the
   * link got recorded as the remembered cursor for the screen being LEFT.
   * Going back to the library then faithfully restored focus to the link
   * that takes you away from the library — the cursor slid to Live TV on
   * arrival while the page stayed put, which looks like the app disagreeing
   * with itself.
   *
   * It is also wrong in principle. This remembers where you were in a
   * four-hundred-film grid. The navigation bar is in the same place on every
   * screen and is never lost; it does not need remembering, and recording it
   * only ever captures the moment of departure.
   */
  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      const id = el?.dataset?.nav;
      if (!id) return;
      if (el?.closest('[data-nav-zone]')) return;   // chrome, not content
      noteFocus(key, id);
    };
    document.addEventListener('focusin', onFocus, true);
    return () => document.removeEventListener('focusin', onFocus, true);
  }, [key]);

  /*
   * THE PERSON ALWAYS WINS. If they press anything while the page is still
   * settling, that press decides where the cursor goes and the restore is
   * abandoned — otherwise the screen would yank the cursor out from under
   * somebody who had already started moving, which is worse than landing at
   * the top would have been.
   */
  useEffect(() => {
    document.addEventListener('keydown', cancelRestore, true);
    document.addEventListener('pointerdown', cancelRestore, true);
    return () => {
      document.removeEventListener('keydown', cancelRestore, true);
      document.removeEventListener('pointerdown', cancelRestore, true);
    };
  }, []);

  /*
   * On arrival, put it back — over the next few frames, as content renders.
   *
   * WITH ONE EXCEPTION: the very first screen of a launch starts on the
   * navigation bar rather than on a poster.
   *
   * The argument for a poster is that resuming is the commonest thing anybody
   * does, so ENTER should do it. The argument that won is that it is not the
   * ONLY thing: somebody opening the app to search for a film, or to ask for
   * one, would first have to climb out of a row of things they had already
   * started. Landing on the navigation makes every destination one step away
   * and none of them presumed.
   *
   * Only on launch. Coming back to the library later restores wherever you
   * were, which is the whole point of the rest of this file.
   */
  useEffect(() => {
    restoreFocus(key, firstScreen ? 'nav-library' : undefined);
    firstScreen = false;
    return cancelRestore;
  }, [key]);
}
